import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, extractApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { AuthShell } from './AcceptInvitation';
import { Check, AlertCircle } from 'lucide-react';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Missing verification token');
      return;
    }
    api
      .post('/api/auth/verify-email', { token })
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setError(extractApiError(err).message);
      });
  }, [token]);

  return (
    <AuthShell>
      {status === 'loading' && (
        <div className="animate-fade-in space-y-4">
          <div className="skeleton h-10 w-10 rounded-lg" />
          <div className="skeleton h-8 w-48" />
          <div className="skeleton h-4 w-64" />
        </div>
      )}
      {status === 'success' && (
        <div className="animate-fade-in space-y-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            <Check className="h-5 w-5" />
          </div>
          <h2 className="font-display text-2xl">Email verified</h2>
          <p className="text-sm text-muted-foreground">
            Your email is confirmed. You can now sign in.
          </p>
          <Button className="w-full" onClick={() => navigate('/login')}>Go to sign-in</Button>
        </div>
      )}
      {status === 'error' && (
        <div className="space-y-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <AlertCircle className="h-5 w-5" />
          </div>
          <h2 className="font-display text-2xl">Verification failed</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button asChild variant="outline"><a href="/login">Back to sign-in</a></Button>
        </div>
      )}
    </AuthShell>
  );
}
